import { decode } from '@googlemaps/polyline-codec';
import { memo, useEffect } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { PATH_ARROW_LAYER_STYLE, PATH_LAYER_STYLE, PATH_POINTS_LAYER_STYLE } from '../../../constants/map';
import { useCamera } from '../../../hooks/useCamera';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getPath } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activePathState,
  pathState,
  reportEndDateState,
  reportSelectedAssetIdsState,
  reportStartDateState,
  selectedPathIdState,
  selectedPathState,
  updatePathState,
  updateReportState,
} from '../../../storage/reports';

function PathLayer() {
  const { map } = useMap();
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const pathItem = useRecoilValue(selectedPathState);
  const activePath = useRecoilValue(activePathState);
  const resetSelectedPathId = useResetRecoilState(selectedPathIdState);
  const { fitToLngLats, zoomTo } = useCamera();
  const [path, setPath] = useRecoilState(pathState);
  const resetPath = useResetRecoilState(pathState);
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const updateReport = useRecoilValue(updateReportState);
  const updatePath = useRecoilValue(updatePathState);

  useEffect(() => {
    return () => {
      resetPath();
    };
  }, []);

  useUpdateEffect(() => {
    resetSelectedPathId();
    resetPath();
  }, [selectedAssetIds, endDate, startDate, updateReport, activePath]);

  useEffect(() => {
    if (!map) return;
    const arrowUrl = '/img/icons/map/arrow.png';
    map.loadImage(`${arrowUrl}`, (error, image) => {
      if (error) {
        return;
      }
      if (image && !map.hasImage('arrow')) {
        map.addImage('arrow', image);
      }
    });
  }, [map]);

  useEffect(() => {
    if (!pathItem?._id || !apiParams) {
      setPath(undefined);
      return;
    }
    const loadPath = async () => {
      const { path, error } = await getPath(
        pathItem._id,
        apiParams.clientId,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
      );
      if (!error && path) {
        setPath(path);
      } else {
        setPath(undefined);
      }
    };
    loadPath();
  }, [pathItem?._id, updatePath]);

  useEffect(() => {
    if (path?.encodedPolyline) {
      const lineStringCoordinates = decode(path.encodedPolyline).map((point) => {
        return [point[1], point[0]];
      });
      if (lineStringCoordinates.length > 1) {
        fitToLngLats(lineStringCoordinates);
      } else if (lineStringCoordinates.length === 1) {
        zoomTo({
          lng: lineStringCoordinates[0][0],
          lat: lineStringCoordinates[0][1],
        });
      }
    }
  }, [path?.encodedPolyline, zoomTo, fitToLngLats]);

  const lineString: GeoJSON.LineString | undefined = path?.encodedPolyline
    ? {
        type: 'LineString',
        coordinates: decode(path.encodedPolyline).map((point) => {
          return [point[1], point[0]];
        }),
      }
    : undefined;

  if (!lineString) return null;

  return (
    <Source
      type="geojson"
      data={lineString}
      id="path"
    >
      <Layer {...PATH_LAYER_STYLE} />
      <Layer {...PATH_ARROW_LAYER_STYLE} />
      <Layer {...PATH_POINTS_LAYER_STYLE} />
    </Source>
  );
}

export default memo(PathLayer);
